import type {
    OptimalVariable,
    OptimisationInputs,
    OptimisationRun,
} from '@/dashboards/shared/optimisation/optimisationSession';
import type { OptimisationBase } from '@/optimizer';

export interface ZeroInvestmentAdjustmentInfo {
    channelName: string;
    previousBudget: number;
    adjustedBudget: number;
}

export type TargetLimits = { min: number; max: number };

// `null` budget means "ask the optimiser for its default". The default is a spend, so a KPI
// base without its own target starts as a budget run rather than feeding a spend in as a KPI.
export const resolveBootstrapTarget = (search: {
    budget?: number;
    optimizationBase?: OptimisationBase;
}): { optimizationBase: OptimisationBase; budget: number | null } => {
    if (search.budget) {
        return {
            optimizationBase: search.optimizationBase ?? 'budget',
            budget: search.budget,
        };
    }
    return { optimizationBase: 'budget', budget: null };
};

// Value pre-filled into the target input when the user picks another base. Seeded from the
// committed run only, so it always matches what the page is currently showing.
export const targetForBase = (
    base: OptimisationBase,
    committed: OptimisationRun | null,
    previousTotalContribution: number | undefined,
): number | undefined => {
    if (!committed) return undefined;
    if (committed.inputs.optimizationBase === base) {
        return committed.inputs.budget;
    }

    const { result } = committed;
    if (base === 'media_kpi') {
        return (
            result.mediaKpi ??
            Object.values(
                result.optimalVariables as Record<string, OptimalVariable>,
            ).reduce((sum, variable) => sum + (variable.kpi || 0), 0)
        );
    }
    if (base === 'total_kpi') {
        return result.totalKpi || previousTotalContribution;
    }
    return result.defaultBudget;
};

// Limits and budgetError describe the committed run's own base; for any other base they are
// in different units and must not be shown.
export const committedTargetFeedback = (
    committed: OptimisationRun | null,
    base: OptimisationBase,
): { limits: TargetLimits | null; budgetError: string | undefined } => {
    if (!committed || committed.inputs.optimizationBase !== base) {
        return { limits: null, budgetError: undefined };
    }
    const { lowestBudget, highestBudget, budgetError } = committed.result;
    return {
        limits:
            lowestBudget != null && highestBudget != null
                ? { min: lowestBudget, max: highestBudget }
                : null,
        budgetError: budgetError || undefined,
    };
};

// Locking a channel at 0 lowers the optimiser's ceiling; the optimiser then clamps the
// requested target to it. Explain that clamp to the user instead of silently changing it.
export const detectZeroInvestmentAdjustment = ({
    previous,
    requested,
    run,
    variableNameMap,
}: {
    previous: OptimisationRun | null;
    requested: OptimisationInputs;
    run: OptimisationRun;
    variableNameMap: Record<string, string>;
}): ZeroInvestmentAdjustmentInfo | null => {
    const { highestBudget } = run.result;
    const previousHighest = previous?.result.highestBudget;
    if (
        !previous ||
        previous.inputs.optimizationBase !== requested.optimizationBase ||
        highestBudget == null ||
        previousHighest == null ||
        requested.budget <= highestBudget ||
        highestBudget >= previousHighest
    ) {
        return null;
    }

    const zeroChannel = Object.entries(requested.channels).find(
        ([, channel]) => channel.locked && Number(channel.investment) === 0,
    );
    if (!zeroChannel) return null;

    const [channelKey] = zeroChannel;
    return {
        channelName: variableNameMap[channelKey] || channelKey,
        previousBudget: requested.budget,
        adjustedBudget: highestBudget,
    };
};
